import { useCallback, useSyncExternalStore } from "react";

// A router, rather than a routing library.
//
// There are six paths. A dependency to resolve six paths would be more code to read than
// this file, and the History API already holds the state -- useSyncExternalStore subscribes
// to it directly rather than mirroring it into a store that could drift.

const listeners = new Set<() => void>();
const DEPTH_KEY = "__jtarchiveDepth";
const OVERLAYS = '[role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"]';

function routeDepth(): number {
  const state: unknown = window.history.state;
  if (typeof state !== "object" || state === null) return 0;
  const depth = (state as Record<string, unknown>)[DEPTH_KEY];
  return typeof depth === "number" && depth > 0 ? depth : 0;
}

function androidBack(event: Event): void {
  const open = Array.from(document.querySelectorAll<HTMLElement>(OVERLAYS)).filter(
    (element) =>
      !element.hidden &&
      element.getAttribute("aria-hidden") !== "true" &&
      element.getClientRects().length > 0,
  );
  if (open.length > 0) {
    event.preventDefault();
    const active = document.activeElement;
    (active instanceof HTMLElement && open.some((element) => element.contains(active))
      ? active
      : open[open.length - 1]!
    ).dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true }));
    return;
  }
  if (routeDepth() > 0) {
    event.preventDefault();
    window.history.back();
  }
}

if (typeof window !== "undefined") window.addEventListener("jtarchive:back", androidBack);

function notify(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  // popstate covers Back and Forward; navigate() notifies its own listeners, because
  // pushState deliberately does not fire an event.
  window.addEventListener("popstate", listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("popstate", listener);
  };
}

function snapshot(): string {
  return window.location.pathname;
}

export function navigate(path: string, { replace = false } = {}): void {
  if (path === window.location.pathname) return;
  const state =
    typeof window.history.state === "object" && window.history.state !== null
      ? window.history.state
      : {};
  const next = { ...state, [DEPTH_KEY]: replace ? routeDepth() : routeDepth() + 1 };
  if (replace) window.history.replaceState(next, "", path);
  else window.history.pushState(next, "", path);
  notify();
}

export function usePath(): string {
  // Server snapshot returns "/" so this module can be imported without a DOM, which keeps
  // it usable from a test that only wants `navigate`.
  return useSyncExternalStore(subscribe, snapshot, () => "/");
}

export interface LinkActivation {
  readonly button: number;
  readonly altKey: boolean;
  readonly ctrlKey: boolean;
  readonly metaKey: boolean;
  readonly shiftKey: boolean;
  readonly defaultPrevented: boolean;
}

export function isPlainLinkActivation(event: LinkActivation): boolean {
  return (
    !event.defaultPrevented &&
    event.button === 0 &&
    !event.altKey &&
    !event.ctrlKey &&
    !event.metaKey &&
    !event.shiftKey
  );
}

/** A link that routes in-page but is still a real anchor: middle-click and copy work. */
export function useLink(path: string): {
  href: string;
  onClick: (event: React.MouseEvent) => void;
} {
  const onClick = useCallback(
    (event: React.MouseEvent) => {
      // Leave the browser to handle anything that means "open this somewhere else".
      if (!isPlainLinkActivation(event)) return;
      event.preventDefault();
      navigate(path);
    },
    [path],
  );
  return { href: path, onClick };
}
